import React, { useState } from 'react';
import {
  ArrowUpRightIcon,
  CloseIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@iconicicons/react';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { PointsTable } from '../points/PointsTable';
import { PointFilters } from '../points/PointFilters';
import { CapacityComponentTable } from '../capacityComponents/CapacityComponentTable';
import { CapacityComponentFilters } from '../capacityComponents/CapacityComponentFilters';
import { ReusableModal } from '../modals/ReusableModal';
import { MoveIcon } from '../icons/MoveIcon';
import {
  NoticeComponentTable,
  NoticeComponentFilters,
} from '../noticeComponents';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectShowNoticeComponents,
  selectShowPointCapacityComponents,
  selectShowPriceComponents,
  selectShowSegmentCapacityComponents,
} from '../plotter/plotterSelectors';
import { plotterActions } from '../plotter/plotterSlice';
import { PriceComponentTable } from '../priceComponents/PriceComponentTable';
import { PriceComponentFilters } from '../priceComponents/PriceComponentFilter';
import { RegionTable } from '../regions/RegionTable';
import { RegionAssignmentTable } from '../regions/RegionAssignmentTable';

export const MapBuilder = () => {
  const dispatch = useDispatch();

  const showSegmentCapacityComponents = useSelector(
    selectShowSegmentCapacityComponents,
  );
  const showPointCapacityComponents = useSelector(
    selectShowPointCapacityComponents,
  );
  const showPriceComponents = useSelector(selectShowPriceComponents);
  const showNoticeComponents = useSelector(selectShowNoticeComponents);

  const [regionMode, setRegionMode] = useState<'table' | 'assignment'>('table');

  const [isOpen, setIsOpen] = useState<boolean>(false);
  return (
    <>
      <div>
        <h4>Map Builder</h4>
        <div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="toggle-price-components"
              checked={showPriceComponents}
              onChange={(evt) =>
                dispatch(
                  plotterActions.setShowPriceComponents(evt.target.checked),
                )
              }
              role="button"
            />
            <label
              className="form-check-label"
              htmlFor="toggle-price-components"
              role="button"
            >
              Display Market Locations
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="toggle-segment-capacities"
              checked={showSegmentCapacityComponents}
              onChange={(evt) =>
                dispatch(
                  plotterActions.setShowSegmentCapacityComponents(
                    evt.target.checked,
                  ),
                )
              }
              role="button"
            />
            <label
              className="form-check-label"
              htmlFor="toggle-segment-capacities"
              role="button"
            >
              Display Segment Capacities
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="toggle-point-capacities"
              checked={showPointCapacityComponents}
              onChange={(evt) =>
                dispatch(
                  plotterActions.setShowPointCapacityComponents(
                    evt.target.checked,
                  ),
                )
              }
              role="button"
            />
            <label
              className="form-check-label"
              htmlFor="toggle-point-capacities"
              role="button"
            >
              Display Point Capacities
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="toggle-notice-components"
              checked={showNoticeComponents}
              onChange={(evt) =>
                dispatch(
                  plotterActions.setShowNoticeComponents(evt.target.checked),
                )
              }
              role="button"
            />
            <label
              className="form-check-label"
              htmlFor="toggle-notice-components"
              role="button"
            >
              Display Notice Components
            </label>
          </div>
        </div>
        <hr />
        <div>
          <h5>Shortcuts</h5>
          <ul>
            <li>
              <kbd>Shift</kbd> + Click Map: Add a node
            </li>
            <li>
              <kbd>Shift</kbd> + Click Node: Add an edge
            </li>
            <li>
              <kbd>Esc</kbd>: Cancel edge creation
            </li>
          </ul>
        </div>
        <div>
          <button
            className="btn btn-secondary my-3"
            onClick={() => setIsOpen(true)}
          >
            Instructions
          </button>
        </div>
      </div>
      <ReusableModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Map Builder Instructions"
        size="lg"
        header={<span>Map Builder Instructions</span>}
      >
        <div>
          <details>
            <summary className="fs-5">Add a node</summary>
            <p className="my-3">
              Click anywhere on the map or hold down <kbd>Shift</kbd> and click
              on the map to add a new node. Select the locations that should be
              placed on that node and click the check mark to confirm the
              creation.
            </p>
          </details>
          <details>
            <summary className="fs-5">Move a node</summary>
            <p className="my-3">
              {' '}
              Click on the node you want to move and then click the <MoveIcon />{' '}
              icon. Then click on where you want to move the node. This will
              move the node to the new location.
            </p>
          </details>
          <details>
            <summary className="fs-5">Remove a node</summary>
            <p className="my-3">
              {' '}
              Click on the node you want to remove and then click the{' '}
              <TrashIcon /> icon. This will remove the node from the map.
            </p>
          </details>
          <details>
            <summary className="fs-5">Attach a location to a node</summary>
            <p className="my-3">
              Click on the node you want to attach a location to and then use
              the search box to find the location you want to attach. Click the
              location to confirm the update.
            </p>
          </details>
          <details>
            <summary className="fs-5">Remove a location from a node</summary>
            <p className="my-3">
              Click on the node you want to remove a location from and then
              click the <CloseIcon /> icon next to the location you want to
              remove.
            </p>
          </details>
          <details>
            <summary className="fs-5">Link nodes with edges</summary>
            <p className="my-3">
              {' '}
              Click on the first node you want to link and then click on the{' '}
              <ArrowUpRightIcon /> icon or hold down <kbd>Shift</kbd> and click
              the node to enter the edge creation mode. Click on the second node
              to create an edge between the two nodes. The flow direction will
              be from the start node to the end node. Clicking on other nodes
              will continue to create edges. If the nodes are in different
              pipelines the edge will be an interconnect. Press <kbd>Esc</kbd>{' '}
              or click anywhere other than a node to cancel the edge creation.
            </p>
          </details>
          <details>
            <summary className="fs-5">Create a location over an edge</summary>
            <p className="my-3">
              It is possible to split an edge by creating a location directly
              over it. To do this, hold down <kbd>Shift</kbd> and click on the
              edge where you want to create the location, then create the
              location normally. Alternatively, you can click on the edge to
              select it, then click the <PlusCircleIcon /> icon to create a
              location over the edge.
            </p>
          </details>
          <details>
            <summary className="fs-5">Remove an edge</summary>
            <p className="my-3">
              {' '}
              Click on the edge you want to remove and then click the{' '}
              <TrashIcon /> icon. This will remove the edge from the map.
            </p>
          </details>
          <details>
            <summary className="fs-5">Remove an interconnect</summary>
            <p className="my-3">
              {' '}
              Click on the interconnect you want to remove and then click the{' '}
              <TrashIcon /> icon. This will remove the interconnect from the
              map.
            </p>
          </details>
          <details>
            <summary className="fs-5">Saving changes</summary>
            <p className="my-3"> Changes are saved automatically.</p>
          </details>
        </div>
      </ReusableModal>
      <BelowMapSetter>
        <BelowMapTab tabName="Points" contextKey="points">
          <PointFilters />
          <PointsTable />
        </BelowMapTab>
        <BelowMapTab
          tabName="Capacity Components"
          contextKey="capacity-components"
        >
          <CapacityComponentFilters />
          <CapacityComponentTable />
        </BelowMapTab>
        <BelowMapTab tabName="Notice Components" contextKey="notice-components">
          <NoticeComponentFilters />
          <NoticeComponentTable />
        </BelowMapTab>
        <BelowMapTab tabName="Price Components" contextKey="price-components">
          <PriceComponentFilters />
          <PriceComponentTable />
        </BelowMapTab>
        <BelowMapTab tabName="Regions" contextKey="regions">
          <div>
            <div className="m-3">
              <button
                className={`btn btn-sm ${
                  regionMode === 'table' ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setRegionMode('table')}
              >
                Table View
              </button>
              <button
                className={`btn btn-sm ms-2 ${
                  regionMode === 'assignment'
                    ? 'btn-primary'
                    : 'btn-outline-primary'
                }`}
                onClick={() => setRegionMode('assignment')}
              >
                Assignment View
              </button>
            </div>
            {regionMode === 'table' && <RegionTable />}
            {regionMode === 'assignment' && <RegionAssignmentTable />}
          </div>
        </BelowMapTab>
      </BelowMapSetter>
    </>
  );
};
